"use client"

import type React from "react"
import { useCallback, useState } from "react"
import { Plus, UserCheck } from "lucide-react"
import { Modal } from "../modal.tsx"
import { ComplaintForm } from "../forms/complaintForm.tsx"
import { TaskForm } from "../forms/taskForm.tsx"
import { RecoveryTaskForm } from "../forms/recoveryTaskForm.tsx"
import { InvoiceQuickCreateActions } from "./InvoiceQuickCreateActions.tsx"
import { MODAL_CANCEL_BTN, MODAL_FOOTER, MODAL_PRIMARY_BTN } from "../ui/modalStyles.ts"
import { getToken } from "../../utils/auth.ts"
import axiosInstance from "../../utils/axiosConfig.ts"
import { toast } from "../../utils/notify.ts"
import { createFormDataRequestConfig, getOperationErrorMessage } from "../../utils/crudSubmit.ts"
import {
  buildComplaintFormData,
} from "../../utils/complaintSubmit.ts"

type QuickCreateKind = "complaint" | "task" | "recovery" | null

function validateTaskForm(formData: Record<string, unknown>): string | null {
  if (!formData.task_type) return "Task type is required"
  if (formData.task_type === "recovery") return "Use Recovery Tasks for collection work"
  if (!formData.due_date) return "Due date is required"
  const assigned = Array.isArray(formData.assigned_to)
    ? formData.assigned_to
    : formData.assigned_to
      ? [formData.assigned_to]
      : []
  if (!assigned.length) return "At least one assignee is required"
  return null
}

function validateRecoveryForm(formData: Record<string, unknown>): string | null {
  if (!formData.assigned_to) return "Select an employee"
  const ids = Array.isArray(formData.invoice_ids) ? formData.invoice_ids : []
  if (ids.length === 0) return "Select at least one invoice"
  return null
}

export function useInvoiceQuickCreate() {
  const [activeModal, setActiveModal] = useState<QuickCreateKind>(null)
  const [isLoading, setIsLoading] = useState(false)

  const [complaintForm, setComplaintForm] = useState<Record<string, unknown>>({})
  const [taskForm, setTaskForm] = useState<Record<string, unknown>>({})
  const [recoveryForm, setRecoveryForm] = useState<Record<string, unknown>>({ invoice_ids: [] })

  const openComplaint = useCallback(() => {
    setComplaintForm({})
    setActiveModal("complaint")
  }, [])

  const openTask = useCallback(() => {
    setTaskForm({})
    setActiveModal("task")
  }, [])

  const openRecovery = useCallback(() => {
    setRecoveryForm({ invoice_ids: [] })
    setActiveModal("recovery")
  }, [])

  const closeModal = useCallback(() => {
    setActiveModal(null)
    setComplaintForm({})
    setTaskForm({})
    setRecoveryForm({ invoice_ids: [] })
  }, [])

  const handleComplaintInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target
    setComplaintForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleComplaintFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setComplaintForm((prev) => ({ ...prev, attachment: e.target.files![0] }))
    }
  }

  const handleComplaintCustomerSearch = async (searchTerm: string) => {
    try {
      const token = getToken()
      const response = await axiosInstance.get(
        `/complaints/search-customer?search_term=${encodeURIComponent(searchTerm)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      return response.data
    } catch (error) {
      console.error("Failed to search customer", error)
      toast.error("Failed to search customer")
      return null
    }
  }

  const handleTaskInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
      | { target: { name: string; value: unknown } },
  ) => {
    const { name, value } = e.target
    setTaskForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleRecoveryInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target
    setRecoveryForm((prev) => ({ ...prev, [name]: value }))
  }

  const setRecoveryField = (name: string, value: unknown) => {
    setRecoveryForm((prev) => ({ ...prev, [name]: value }))
  }

  const submitComplaint = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!complaintForm.customer_id) {
      toast.error("Search and select a customer first")
      return
    }
    if (!complaintForm.category) {
      toast.error("Category is required")
      return
    }
    if (!complaintForm.description) {
      toast.error("Description is required")
      return
    }

    setIsLoading(true)
    try {
      const token = getToken()
      const payload = buildComplaintFormData(complaintForm)
      await axiosInstance.post("/complaints/add", payload, createFormDataRequestConfig(token))
      toast.success("Complaint added successfully")
      closeModal()
    } catch (error) {
      console.error("Failed to add complaint", error)
      toast.error(getOperationErrorMessage(error, "Complaint"))
    } finally {
      setIsLoading(false)
    }
  }

  const submitTask = async (e: React.FormEvent) => {
    e.preventDefault()
    const validationError = validateTaskForm(taskForm)
    if (validationError) {
      toast.error(validationError)
      return
    }

    setIsLoading(true)
    try {
      const token = getToken()
      await axiosInstance.post("/tasks/add", taskForm, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      })
      toast.success("Task added successfully")
      closeModal()
    } catch (error) {
      console.error("Failed to add task", error)
      toast.error(getOperationErrorMessage(error, "Task"))
    } finally {
      setIsLoading(false)
    }
  }

  const submitRecovery = async (e: React.FormEvent) => {
    e.preventDefault()
    const validationError = validateRecoveryForm(recoveryForm)
    if (validationError) {
      toast.error(validationError)
      return
    }

    setIsLoading(true)
    try {
      const token = getToken()
      const ids = recoveryForm.invoice_ids as string[]
      const res = await axiosInstance.post(
        "/recovery-tasks/bulk-add",
        {
          invoice_ids: ids,
          assigned_to: recoveryForm.assigned_to,
          notes: recoveryForm.notes,
          status: recoveryForm.status || "pending",
        },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      const body = res.data || {}
      const created = Number(body.total_created || 0)
      const skipped = Number(body.total_skipped || 0)
      if (created === 0) {
        const reason = body.skipped?.[0]?.reason || "No recovery tasks created"
        throw new Error(reason)
      }
      if (skipped > 0) {
        toast.success(`Created ${created}; skipped ${skipped}`)
      } else {
        toast.success(
          created === 1 ? "Recovery task added successfully" : `Created ${created} recovery tasks`,
        )
      }
      closeModal()
    } catch (error: unknown) {
      console.error("Failed to add recovery task", error)
      const message =
        error instanceof Error
          ? error.message
          : getOperationErrorMessage(error, "Recovery Task")
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const renderActions = useCallback(
    (options?: { compact?: boolean }) => (
      <InvoiceQuickCreateActions
        compact={options?.compact}
        onNewComplaint={openComplaint}
        onNewTask={openTask}
        onRecoveryTask={openRecovery}
      />
    ),
    [openComplaint, openTask, openRecovery],
  )

  const modals = (
    <>
      <Modal
        isVisible={activeModal === "complaint"}
        onClose={closeModal}
        title="New Complaint"
        isLoading={isLoading}
        size="xl"
      >
        <form onSubmit={submitComplaint}>
          <ComplaintForm
            formData={complaintForm}
            handleInputChange={handleComplaintInputChange}
            handleFileChange={handleComplaintFileChange}
            isEditing={false}
            handleCustomerSearch={handleComplaintCustomerSearch}
            ticketNumber={null}
            hideSubmitButton
          />
          <div className={MODAL_FOOTER}>
            <button type="button" onClick={closeModal} className={MODAL_CANCEL_BTN} disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className={MODAL_PRIMARY_BTN}>
              {isLoading ? "Saving…" : "Create Complaint"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isVisible={activeModal === "task"}
        onClose={closeModal}
        title="New Task"
        isLoading={isLoading}
        size="lg"
      >
        <form onSubmit={submitTask}>
          <TaskForm formData={taskForm} handleInputChange={handleTaskInputChange} isEditing={false} />
          <div className={MODAL_FOOTER}>
            <button type="button" onClick={closeModal} className={MODAL_CANCEL_BTN} disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className={MODAL_PRIMARY_BTN}>
              {isLoading ? (
                "Saving…"
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Create Task
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isVisible={activeModal === "recovery"}
        onClose={closeModal}
        title="New Recovery Task"
        isLoading={isLoading}
        size="lg"
      >
        <form onSubmit={submitRecovery}>
          <RecoveryTaskForm
            formData={recoveryForm}
            handleInputChange={handleRecoveryInputChange}
            setFormField={setRecoveryField}
            isEditing={false}
          />
          <div className={MODAL_FOOTER}>
            <button type="button" onClick={closeModal} className={MODAL_CANCEL_BTN} disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className={MODAL_PRIMARY_BTN}>
              {isLoading ? (
                "Saving…"
              ) : (
                <>
                  <UserCheck className="h-4 w-4" />
                  Create Recovery Task
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>
    </>
  )

  return { renderActions, modals }
}
